import { useContext } from 'react';
import ReactGA from 'react-ga4';
import AppPropsContext from '../context/appPropsContext';
import { isDEVMode } from '../utils/pcpUtils';

export interface ITrackingEventConfig {
    id: string; // eindeutige ID für das Event (von SSG vorgegeben)
    event: string; // vom Backend individuell konfigurierbar ('' = deaktiviert) z. B. "display_bookingcode_popup"
    action?: string; // z. B. "page_view", "submit", "click"
    category?: string; // z. B. "Appointment", "Navigation"
    label?: string;
    value?: number;
    nonInteraction?: boolean;
    transport?: 'beacon' | 'xhr' | 'image';
    [key: string]: any; // beliebige weitere Felder (z. B. appointmentId)
}

export enum GoogleAnalyticsCategory {
    HOMESCREEN = 'HomeScreen',
    APPOINTMENT_BOOKING = 'AppointmentBooking',
}

export enum GoogleAnalyticsAction {
    CLICK = 'click',
    SUBMIT = 'submit',
    PAGEVIEW = 'page_view',
}

const _isDEVMode = isDEVMode();
// const _isDEVMode = false; //ZUM TESTEN

/* #### Begin GTM / GA enabled #### */

export const isGoogleTagManagerEnabled = (apstate: any): boolean => {
    return !_isDEVMode && !!apstate.googleTagManagerID;
};

export const isGoogleAnalyticsEnabled = (apstate: any): boolean => {
    return !isGoogleTagManagerEnabled(apstate) && !_isDEVMode && !!apstate.googleAnalyticsID;
};

/* #### End GTM / GA enabled #### */

/* #### GTM Initialisierung #### */
export const initGTM = (gtmId: string) => {
    if (!gtmId || document.getElementById('gtm-script')) return;

    const script = document.createElement('script');
    script.id = 'gtm-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    });
};

/* #### GTM Event Push #### */
export const pushToDataLayer = (event: Record<string, any>) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
};

/* #### Zentrale Event-Funktion #### */
export const trackEvent = (eventData: ITrackingEventConfig, apstate: any) => {
    if (isGoogleTagManagerEnabled(apstate)) {
        pushToDataLayer(eventData);
    }

    if (isGoogleAnalyticsEnabled(apstate)) {
        ReactGA.event({
            action: eventData.action || eventData.event,
            category: eventData.category || 'General',
            label: eventData.label,
            value: eventData.value,
            nonInteraction: eventData.nonInteraction,
            transport: eventData.transport,
        });
    }
};

/* #### Haupt Hook um Google Tag Manager / Google Analytics Events zu senden #### */
export const useGoogleAnalytics = () => {
    const { apstate } = useContext(AppPropsContext);

    /**
     * Sendet ein Tracking-Event nur, wenn es explizit vom Backend kommt.
     */
    const trackById = (id: string, additionalData: Record<string, any> = {}) => {
        const config: ITrackingEventConfig | undefined = apstate?.trackingConfig?.find((e) => e.id === id);

        if (!config || config.event.trim() === '') {
            return; // Kein Backend-Event oder Opt-Out → nichts senden
        }

        const merged = {
            ...config,
            ...additionalData,
        };

        trackEvent(merged, apstate);
    };

    return {
        isGoogleAnalyticsEnabled: () => isGoogleAnalyticsEnabled(apstate),
        isGoogleTagManagerEnabled: () => isGoogleTagManagerEnabled(apstate),
        trackById,
    };
};

/* ##### BEISPIELE ##### */

/*

Backend liefert:
[
  {
    "id": "bookingcode_popup_visible",
    "event": "display_bookingcode_popup",
    "action": "page_view",
    "category": "Appointment"
  },
  {
    "id": "confirmed_popup_visible",
    "event": ""
  }
]

Dann passiert folgendes:
trackById('bookingcode_popup_visible', { userId: 'abc123' });
-- Wird gesendet an GA oder GTM

trackById('confirmed_popup_visible');
-- Wird NICHT gesendet (Opt-Out)

trackById('something_not_defined');
-- Wird NICHT gesendet (nicht in Backend enthalten)

*/
